import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo
} from 'react';
import {
  BottomInputArea,
  type BottomInputAreaProps
} from '../../components/BottomInputArea';
import {
  TopHeader,
  type TopHeaderProps,
  type HeaderCapabilityOption
} from '../../components/TopHeader';
import { useDifyCapability } from '../../hooks/useDifyCapability';
import { AiDifyCapability } from '../../types/aiDifyCapability';

interface ChatMessage {
  readonly id: string;
  readonly role: 'user' | 'assistant';
  readonly content: string;
  readonly dateCreated: string;
  readonly status?: 'thinking' | 'done' | 'error';
}


const createId = (): string =>
  `${Date.now()}_${Math.random().toString(16).slice(2)}`;
const nowIso = (): string => new Date().toISOString();

export interface AiChatPageProps extends TopHeaderProps, BottomInputAreaProps {
  readonly businessType?: string;
  readonly businessId?: string | number | null;
  /** 点击历史会话项回调 */
  readonly onSelectConversation?: (id: string) => void;
  /** 点击资源项回调 */
  readonly onSelectResource?: (id: string) => void;
  /** 点击分享按钮回调 */
  readonly onClickShare?: () => void;
}

/** 暴露给父组件的方法 */
export interface AiChatPageRef {
  /** 重置会话：清空消息、回到首页、生成新会话ID */
  sendConversation: () => string;
}


export const HomePage = forwardRef<AiChatPageRef, AiChatPageProps>(({
  modelLabel,
  onClickUpgrade,
  value,
  onClickMic,
  onClickAction1,
  onClickAction2,
  onClickAction3,
  onClickAction4,
  onClickDots,
}, ref) => {
  const [draftText, setDraftText] = useState<string>(value ?? '');
  const [activeConversationId, setActiveConversationId] = useState<string>(() => `conv_${createId()}`);
  const [activeConversationDbId, setActiveConversationDbId] = useState<number | null>(null);

  const [messages, setMessages] = useState<readonly ChatMessage[]>([]);
  const [difyConversationId, setDifyConversationId] = useState<string | undefined>(undefined);


  const {
    capabilities,
    loading: difyLoading,
    error: difyError
  } = useDifyCapability();

  const [currentCapability, setCurrentCapability] = useState<AiDifyCapability | null>(null);
  const [currentCapabilityId, setCurrentCapabilityId] = useState<number | undefined>(undefined);


  /** 发送会话：清空消息、回到首页、生成新会话ID（并清空 DB 会话绑定，让下次发送时新建） */
    const sendConversation = useCallback((): string => {
      const newConvId = `conv_${createId()}`;
      setActiveConversationId(newConvId);
      setActiveConversationDbId(null);
      setMessages([]);
      setDifyConversationId(undefined);
      setDraftText('');
      return newConvId;
    }, []);


  useImperativeHandle(ref, () => ({ sendConversation }), [sendConversation]);


  // 初始化默认能力：只在 capabilities 首次加载成功时设置一次
  useEffect(() => {
    if (!difyLoading && !difyError && capabilities.length > 0) {
      if (currentCapabilityId != null) {
        return;
      }
      const enabledList: readonly AiDifyCapability[] = capabilities.filter(item => item.enabled);
      const initial: AiDifyCapability | undefined = (enabledList.length > 0 ? enabledList[0] : capabilities[0]) ?? undefined;
      if (initial) {
        setCurrentCapability(initial);
        setCurrentCapabilityId(initial.id);
      }
    }
  }, [capabilities, currentCapabilityId, difyLoading, difyError]);


  const renderHome = (): React.ReactNode => {
    return (
      <div className="chat-area home-chat-area">
        <div className="message bot" style={{ animationDelay: '0s' }}>
          <div className="avatar">
            <i className="fas fa-sparkles" />
          </div>
          <div className="message-text-group">
            <div className="message-content">
              Hi 我是服务于自动化、工业控制、数字化工厂的 AI 助手
            </div>
            <span className="timestamp">7:55 PM</span>
          </div>
        </div>

        <div className="message user" style={{ animationDelay: '2s' }}>
          <div className="message-text-group">
            <div className="message-content">
              这个项目我在纠结选西门子还是施耐德，你帮我看看整体方案合不合理？
            </div>
            <span className="timestamp">7:54 PM</span>
          </div>
        </div>

        <div className="message bot" style={{ animationDelay: '5s' }}>
          <div className="avatar">
            <i className="fas fa-sparkles" />
          </div>
          <div className="message-text-group">
            <div className="message-content">
              Sure! 我先给你一个结论级判断：如果你这个项目是中小型产线，西门子
              1200 可能有点偏重；如果是多工位 + 后期扩展，施耐德这套架构风险会更低。
            </div>
            <span className="timestamp">7:55 PM</span>
          </div>
        </div>

        <div className="image-group">
          <img
            src="/agimx/static/media/ai-chatbot8.png"
            className="generated-img img-1"
            alt="3D Scene 3"
          />
          <img
            src="/agimx/static/media/ai-chatbot6.png"
            className="generated-img img-2"
            alt="3D Scene 2"
          />
          <img
            src="/agimx/static/media/ai-chatbot7.png"
            className="generated-img img-3"
            alt="3D Scene 1"
          />
        </div>
        <div style={{ height: 20, flexShrink: 0 }} />
      </div>
    );
  };


  const headerOptions: readonly HeaderCapabilityOption[] = capabilities.map(cap => ({
    id: cap.id,
    name: cap.name,
    type: cap.type
  }));

  const headerModelLabel: string = currentCapability?.name ?? modelLabel ?? '智能助手 v2.6';

  return (
    <div className="chat-layout">
      <div className="chat-main">
        <TopHeader
          modelLabel={headerModelLabel}
          onClickUpgrade={onClickUpgrade}
          capabilities={headerOptions}
          currentCapabilityId={currentCapabilityId}
        /> 

        {renderHome()}

        <BottomInputArea
          value={draftText}
          onClickMic={onClickMic}
          onClickSend={sendConversation}
          onClickAction1={onClickAction1}
          onClickAction2={onClickAction2}
          onClickAction3={onClickAction3}
          onClickAction4={onClickAction4}
          onClickDots={onClickDots}
        />
      </div>
    </div>
  );
});